import Component from '@glimmer/component';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Drawer from 'tio-reimagined/components/drawer';
import { formatCurrency, formatCycleDateLong, formatNumericDate } from 'tio-reimagined/format';
import { eq } from 'tio-ui/utilities';
import {
  PhCheckCircle,
  PhArrowCounterClockwise,
  PhWarningCircle,
  PhHourglass,
  PhClock,
} from 'ember-phosphor-icons';
import type { ContributionPaymentRecord } from 'tio-employee/services/reimagined-data';

/**
 * PaymentHistoryDrawer — Slide-out panel showing per-year payment history
 *
 * Opens from the right when a yearly contribution card is clicked.
 * Lists individual payments grouped by month with status icons matching
 * the Figma design: Sent (green check), Resent (blue arrow), Failed (red warning),
 * In Progress (hourglass), Pending (clock).
 *
 * @example
 * ```gts
 * <PaymentHistoryDrawer
 *   @isOpen={{this.isPaymentHistoryDrawerOpen}}
 *   @onClose={{this.closePaymentHistoryDrawer}}
 *   @year="2025"
 *   @payments={{this.paymentsForSelectedYear}}
 *   @total={{1500}}
 * />
 * ```
 */

type PaymentStatus = ContributionPaymentRecord['status'];

interface PaymentHistoryDrawerSignature {
  Args: {
    /** Whether the drawer is open */
    isOpen: boolean;
    /** Called when the drawer should close */
    onClose: () => void;
    /** Selected year label */
    year: string;
    /** Payments for the selected year */
    payments: ContributionPaymentRecord[];
    /** Total contribution amount for the year */
    total: number;
  };
  Element: HTMLDivElement;
}

function statusLabel(status: PaymentStatus): string {
  switch (status) {
    case 'sent':
      return 'Sent';
    case 'resent':
      return 'Resent';
    case 'failed':
      return 'Failed';
    case 'in_progress':
      return 'In Progress';
    case 'pending':
      return 'Pending';
  }
}

/** Background color for the status chip */
function statusChipBg(status: PaymentStatus): string {
  switch (status) {
    case 'sent':
      return 'bg-[#bbe8e2] text-[#003d35]';
    case 'resent':
      return 'bg-[#d2eaf0] text-[#04343f]';
    case 'failed':
      return 'bg-[#fdd5db] text-[#700013]';
    case 'in_progress':
      return 'bg-blue-100 text-blue-700';
    case 'pending':
      return 'bg-gray-100 text-gray-600';
  }
}

/** Background color for the circular icon container */
function statusIconBg(status: PaymentStatus): string {
  switch (status) {
    case 'sent':
      return 'bg-[#bbe8e2]';
    case 'resent':
      return 'bg-[#d2eaf0]';
    case 'failed':
      return 'bg-[#fdd5db]';
    case 'in_progress':
      return 'bg-blue-100';
    case 'pending':
      return 'bg-gray-100';
  }
}

/** Icon color for the status icon */
function statusIconColor(status: PaymentStatus): string {
  switch (status) {
    case 'sent':
      return 'text-[#007D6D]';
    case 'resent':
      return 'text-[#04343f]';
    case 'failed':
      return 'text-[#700013]';
    case 'in_progress':
      return 'text-blue-600';
    case 'pending':
      return 'text-gray-400';
  }
}

/** Date action label — "paid" for sent/resent, "failed" for failed */
function dateActionLabel(status: PaymentStatus): string {
  switch (status) {
    case 'failed':
      return 'failed';
    default:
      return 'paid';
  }
}

/** Count of successful payments (sent + resent) */
function successfulCount(payments: ContributionPaymentRecord[]): number {
  return payments.filter((p) => p.status === 'sent' || p.status === 'resent').length;
}

/** Format amount always showing cents (e.g. "$125" or "$0.00") */
function formatPaymentAmount(amount: number | undefined | null): string {
  const value = amount ?? 0;
  if (value === 0) {
    return formatCurrency(0, 2);
  }
  return formatCurrency(value);
}

export default class PaymentHistoryDrawer extends Component<PaymentHistoryDrawerSignature> {
  get successfulPaymentCount() {
    return successfulCount(this.args.payments);
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <Drawer @isOpen={{@isOpen}} @onClose={{@onClose}} @size="lg" @placement="right" ...attributes>
      <:header>
        <div class="flex flex-col gap-2">
          <Heading @level={{3}} class="text-2xl font-semibold text-[#231e29]">
            Payment History
            {{@year}}
          </Heading>
          <Text class="text-sm font-medium text-[#6a6670] leading-relaxed">
            {{this.successfulPaymentCount}}
            successful payments
          </Text>
        </div>
      </:header>
      <:body>
        <div class="flex flex-col gap-3 py-2">
          {{#each @payments as |payment|}}
            <div
              class="bg-white border border-[#e1e0e3] rounded-2xl p-4 flex items-center justify-between"
            >
              {{! Left: Status icon + payment details }}
              <div class="flex items-center gap-3 min-w-0 flex-1">
                <div
                  class="w-8 h-8 rounded-full flex items-center justify-center shrink-0
                    {{statusIconBg payment.status}}"
                >
                  {{#if (eq payment.status "sent")}}
                    <PhCheckCircle
                      @size={{24}}
                      @weight="fill"
                      class={{statusIconColor payment.status}}
                    />
                  {{else if (eq payment.status "resent")}}
                    <PhArrowCounterClockwise
                      @size={{20}}
                      @weight="bold"
                      class={{statusIconColor payment.status}}
                    />
                  {{else if (eq payment.status "failed")}}
                    <PhWarningCircle
                      @size={{24}}
                      @weight="fill"
                      class={{statusIconColor payment.status}}
                    />
                  {{else if (eq payment.status "in_progress")}}
                    <PhHourglass
                      @size={{20}}
                      @weight="fill"
                      class={{statusIconColor payment.status}}
                    />
                  {{else}}
                    <PhClock @size={{20}} @weight="fill" class={{statusIconColor payment.status}} />
                  {{/if}}
                </div>
                <div class="flex flex-col gap-1.5 min-w-0">
                  <Text class="text-base font-medium text-[#35303a] truncate">
                    {{formatCycleDateLong payment.benefitCycle}}
                  </Text>
                  <div class="flex items-center gap-1.5 text-sm text-[#6a6670]">
                    <span>{{payment.servicer}}</span>
                    {{#if payment.paidDate}}
                      <div class="w-1.5 h-1.5 rounded-full bg-[#6a6670]"></div>
                      <span>{{dateActionLabel payment.status}}
                        {{formatNumericDate payment.paidDate}}</span>
                    {{/if}}
                  </div>
                </div>
              </div>

              {{! Right: Amount + Status chip }}
              <div class="flex flex-col items-end gap-1 shrink-0">
                <Text class="text-base font-semibold text-[#4c4950]">
                  {{formatPaymentAmount payment.amount}}
                </Text>
                <span
                  class="text-[10px] font-semibold leading-relaxed px-2 py-0.5 rounded-lg
                    {{statusChipBg payment.status}}"
                >
                  {{statusLabel payment.status}}
                </span>
              </div>
            </div>
          {{else}}
            <div class="py-10 flex flex-col items-center justify-center gap-2 text-center">
              <Text class="text-sm text-[#6a6670]">No payments recorded for
                {{@year}}.</Text>
            </div>
          {{/each}}
        </div>
      </:body>
    </Drawer>
  </template>
}
